import {
  cloneDeep,
  each,
  get,
  isEmpty,
  isEqual,
  isUndefined,
  map,
  set,
  size,
  update,
  has,
  isNil,
} from 'lodash';

import vifs from 'common/authoring_workflow/vifs';
import baseVifReducer from './base.js';

import * as actions from '../../actions.js';
import { forEachSeries, createDefaultHierarchy } from 'common/authoring_workflow/helpers';
import { OrderConfig, Vif, Hierarchy } from 'common/visualizations/vif';
import { ViewColumn } from 'common/types/viewColumn';

export default function agTable(state: any, action: any) {
  if (isUndefined(state)) {
    return vifs().agTable;
  }

  state = cloneDeep(state);

  switch (action.type) {
    case actions.RESET_STATE: {
      state = vifs().agTable;
      break;
    }

    case actions.REPLACE_VIF: {
      state = action.vif;
      break;
    }

    case actions.SET_TABLE_COLUMNS: {
      const { tableColumns } = action;
      const path = ['series', 0, 'dataSource', 'dimension', 'columns'];
      set(state, path, tableColumns);
      break;
    }

    case actions.SET_COLUMN_ORDER_CONFIGURATION: {
      const { order } = action;
      set(state, 'configuration.order', order);
      break;
    }

    case actions.SET_TABLE_VIZ_COLUMN_FORMATS: {
      const { columnFormats } = action;
      set(state, ['series', 0, 'formatting', 'columnFormat'], columnFormats);
      break;
    }

    case actions.UPDATE_TABLE_VIZ_COLUMN_FORMAT: {
      const { updatedColumnFormat, columnName } = action;
      set(state, ['series', 0, 'formatting', 'columnFormat', columnName], updatedColumnFormat);
      break;
    }

    case actions.UPDATE_TABLE_VIZ_COLUMN_STYLE: {
      const { updatedColumnFormatStyle, columnName } = action;
      set(state, ['series', 0, 'formatting', 'columnFormat', columnName, 'style'], updatedColumnFormatStyle);
      break;
    }

    case actions.UPDATE_TABLE_VIZ_COLUMN_STYLE_CONDITION: {
      const { updatedColumnFormatStyle, columnName } = action;
      set(
        state,
        ['series', 0, 'formatting', 'columnFormat', columnName, 'conditionStyle'],
        updatedColumnFormatStyle,
      );
      break;
    }

    case actions.UPDATE_TABLE_VIZ_HEADER_STYLE: {
      const { updatedHeaderFormatStyle } = action;
      set(state, ['series', 0, 'formatting', 'headerFormat'], updatedHeaderFormatStyle);
      break;
    }

    case actions.UPDATE_TABLE_VIZ_HEADER_TEXT_STYLE: {
      const { updatedHeaderFormatTextStyle } = action;
      set(state, ['series', 0, 'formatting', 'headerFormat', 'fontStyle'], updatedHeaderFormatTextStyle);
      break;
    }

    case actions.SET_ACTIVE_HIERARCHY_ID: {
      const { hierarchyId } = action;
      set(state, ['series', 0, 'dataSource', 'activeHierarchyId'], hierarchyId);
      break;
    }

    case actions.NEW_TABLE_HIERARCHY:
      forEachSeries(state, (series: Vif['series'][number]) => {
        const hierarchiesIndex = size(get(series, ['dataSource', 'hierarchies']));
        const newHierarchy = createDefaultHierarchy();
        set(series, ['dataSource', 'hierarchies', hierarchiesIndex], newHierarchy);

        // make the new hierarchy active
        set(series, ['dataSource', 'activeHierarchyId'], newHierarchy.id);
      });
      break;

    case actions.SET_TABLE_HIERARCHIES: {
      const { tableHierarchies } = action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        set(series, 'dataSource.hierarchies', tableHierarchies);
      });
      break;
    }

    case actions.REMOVE_TABLE_HIERARCHY: {
      const hierarchiesIndex = action.index;

      forEachSeries(state, (series: Vif['series'][number]) => {
        // For columns that were in a removed hierarchy, set "hide" property to false
        const removedHierarchy: Hierarchy = get(series, `dataSource.hierarchies[${hierarchiesIndex}]`, null);
        if (removedHierarchy) {
          const columnsInRemovedHierarchy = map(removedHierarchy.columnConfigurations, 'columnName');
          each(get(series, 'dataSource.dimension.columns', []) as ViewColumn[], (column) => {
            if (column.fieldName && columnsInRemovedHierarchy.includes(column.fieldName)) {
              set(column, 'hide', false);
            }
          });
        }

        // filtering out the hierarchy array at the index specified
        // setting the hierarchy object to the newly filtered out array
        const filteredHierarchies = get(series, 'dataSource.hierarchies', []).filter(
          (h: Hierarchy, idx: number) => idx !== hierarchiesIndex,
        );
        set(series, ['dataSource', 'hierarchies'], filteredHierarchies);

        // make the hierarchy that now lives at `action.index` as active, unless that's now an invalid index
        // (if the removed hierarchy was the last in the list, the index would be invalid); in that case, make
        // the last hierarchy active
        const nextActiveHierarchyIndex =
          hierarchiesIndex >= filteredHierarchies.length ? filteredHierarchies.length - 1 : hierarchiesIndex;
        const nextActiveHierarchyId = get(series, [
          'dataSource',
          'hierarchies',
          nextActiveHierarchyIndex,
          'id',
        ]);
        set(series, ['dataSource', 'activeHierarchyId'], nextActiveHierarchyId);
      });

      break;
    }

    case actions.DUPLICATE_TABLE_HIERARCHY: {
      const { id, index } = action;

      forEachSeries(state, (series: Vif['series'][number]) => {
        const nextHierarchies = cloneDeep(get(series, 'dataSource.hierarchies', []));
        const duplicate = cloneDeep(
          get(series, ['dataSource', 'hierarchies', index], {
            columnConfigurations: [],
          }),
        );

        duplicate.id = id;

        // place the new hierarchy directly after the source hierarchy
        nextHierarchies.splice(index + 1, 0, duplicate);
        set(series, ['dataSource', 'hierarchies'], nextHierarchies);

        // make the duplicate hierarchy active
        set(series, ['dataSource', 'activeHierarchyId'], duplicate.id);
      });
      break;
    }

    case actions.UPDATE_TABLE_HIERARCHY_NAME: {
      const { hierarchyIndex, name } = action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        set(series, ['dataSource', 'hierarchies', hierarchyIndex, 'name'], name);

        // mark the updated hierarchy as active
        set(
          series,
          ['dataSource', 'activeHierarchyId'],
          get(series, ['dataSource', 'hierarchies', hierarchyIndex, 'id']),
        );
      });
      break;
    }

    case actions.SHOW_GRAND_TOTAL: {
      const { hierarchyIndex, isShowing } = action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        set(series, ['dataSource', 'hierarchies', hierarchyIndex, 'showGrandTotal'], isShowing);

        // mark the updated hierarchy as active
        set(
          series,
          ['dataSource', 'activeHierarchyId'],
          get(series, ['dataSource', 'hierarchies', hierarchyIndex, 'id']),
        );
      });
      break;
    }

    case actions.SET_TABLE_ROW_STRIPING: {
      set(state, 'configuration.rowStripeStyle.base.text', action.tableRowStripeStyle?.base?.text);
      set(state, 'configuration.rowStripeStyle.base.fill', action.tableRowStripeStyle?.base?.fill);
      set(state, 'configuration.rowStripeStyle.alternate.text', action.tableRowStripeStyle?.alternate?.text);
      set(state, 'configuration.rowStripeStyle.alternate.fill', action.tableRowStripeStyle?.alternate?.fill);
      break;
    }

    case actions.SHOW_SUB_TOTAL: {
      const { hierarchyIndex, isShowing } = action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        set(series, ['dataSource', 'hierarchies', hierarchyIndex, 'showSubTotal'], isShowing);

        // mark the updated hierarchy as active
        set(
          series,
          ['dataSource', 'activeHierarchyId'],
          get(series, ['dataSource', 'hierarchies', hierarchyIndex, 'id']),
        );
      });
      break;
    }

    case actions.SHOW_NET_TOTAL: {
      const { hierarchyIndex, isShowing } = action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        set(series, ['dataSource', 'hierarchies', hierarchyIndex, 'showNetTotal'], isShowing);

        // mark the updated hierarchy as active
        set(
          series,
          ['dataSource', 'activeHierarchyId'],
          get(series, ['dataSource', 'hierarchies', hierarchyIndex, 'id']),
        );
      });
      break;
    }

    case actions.UPDATE_CUSTOM_LABELS: {
      const { hierarchyIndex, customLabels } = action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        set(series, ['dataSource', 'hierarchies', hierarchyIndex, 'customLabels'], customLabels);

        // mark the updated hierarchy as active
        set(
          series,
          ['dataSource', 'activeHierarchyId'],
          get(series, ['dataSource', 'hierarchies', hierarchyIndex, 'id']),
        );
      });
      break;
    }

    case actions.UPDATE_CUSTOM_FORMULAS: {
      const { hierarchyIndex, customFormulas } = action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        // Update custom formulas for each column in the hierarchy
        const columnConfigurations = get(
          series,
          ['dataSource', 'hierarchies', hierarchyIndex, 'columnConfigurations'],
          [],
        );
        columnConfigurations.forEach((columnConfig: any, columnIndex: number) => {
          // Check if this column has a custom formula to apply
          if (customFormulas[columnConfig.columnName]) {
            // Set custom formula for matching column name
            set(
              series,
              [
                'dataSource',
                'hierarchies',
                hierarchyIndex,
                'columnConfigurations',
                columnIndex,
                'customFormula',
              ],
              customFormulas[columnConfig.columnName],
            );
          } else if (columnConfig.customFormula) {
            // Clear custom formula if not in the new customFormulas object
            set(
              series,
              [
                'dataSource',
                'hierarchies',
                hierarchyIndex,
                'columnConfigurations',
                columnIndex,
                'customFormula',
              ],
              '',
            );
          }
        });
      });
      break;
    }

    case actions.SET_NET_TOTAL_CALC: {
      // its almost like this is a TS file, and TS is something we could use 😮🤔
      const hierarchyIndex: number = action.hierarchyIndex;
      const netTotalCalc: string = action.netTotalCalc;
      const columnName: string = action.columnName;

      forEachSeries(state, (series: Vif['series'][number]) => {
        const configs = series.dataSource.hierarchies?.[hierarchyIndex].columnConfigurations;
        const config = configs?.find((c) => c.columnName === columnName); // this is the real reason I did TS
        if (config) {
          config.netTotalCalc = netTotalCalc;
        }

        // mark the updated hierarchy as active
        set(
          series,
          ['dataSource', 'activeHierarchyId'],
          get(series, ['dataSource', 'hierarchies', hierarchyIndex, 'id']),
        );
      });
      break;
    }

    case actions.SET_TABLE_INDENTED_LAYOUT: {
      set(state, 'configuration.isIndented', action.toggle);
      break;
    }

    case actions.SET_SHOW_TABLE_ACTIONS: {
      const { showAction } = action;
      set(state, 'configuration.showProcessDiscoveryAction', showAction);
      break;
    }

    case actions.SET_SHOW_EXISTING_ACTION_COLUMN: {
      const { showExistingActionColumn } = action;
      set(state, 'configuration.showExistingActionColumn', showExistingActionColumn);
      break;
    }

    case actions.SET_SELECTED_EXISTING_COLUMN: {
      const { selectedExistingColumn } = action;
      set(state, 'configuration.selectedExistingColumn', selectedExistingColumn);
      break;
    }

    case actions.SET_NEW_TABLE_ACTIONS: {
      const { newTableActions } = action;
      set(state, 'configuration.newTableActions', newTableActions);
      break;
    }

    case actions.SET_SELECTED_ACTION_FOR_EXISTING_COLUMN: {
      const { selectedActionForExistingColumn } = action;
      set(state, 'configuration.selectedActionForExistingColumn', selectedActionForExistingColumn);
      break;
    }

    case actions.SET_TABLE_ACTIONS_MEDIA_TYPE: {
      const { mediaType } = action;
      if (mediaType) {
        set(state, 'configuration.actionsMediaType', mediaType);
      } else {
        // Remove the property if mediaType is empty
        const config = get(state, 'configuration');
        if (config && has(config, 'actionsMediaType')) {
          delete config.actionsMediaType;
        }
      }
      break;
    }

    case actions.SET_TABLE_ACTIONS_CONTEXT_FIELDS_MAPPING: {
      const { contextFieldMappings } = action;
      if (contextFieldMappings && contextFieldMappings.length > 0) {
        set(state, 'configuration.contextFieldMappings', contextFieldMappings);
      } else {
        // Remove the property if contextFieldMappings is empty
        const config = get(state, 'configuration');
        if (config && has(config, 'contextFieldMappings')) {
          delete config.contextFieldMappings;
        }
      }
      break;
    }

    case actions.SET_TABLE_CUSTOM_SORT: {
      const { hierarchyId, columnName, rowData } = action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        const hierarchies = get(series, ['dataSource', 'hierarchies'], []);
        const hierarchyIndex = hierarchies.findIndex((hierachy) => hierachy.id === hierarchyId);
        if (hierarchyIndex < 0) {
          // hierarchies.findIndex() will return -1 if there are no hierarchies found.
          // This if-block is done to prevent negative indices in the hierarchies array.
          // If the custom sort configuration does not apply due to this if-block,
          // it might be due to the vif not having hierarchies, which is something worse.
          return;
        }

        if (isEmpty(rowData)) {
          const existingConfig = get(series, [
            'dataSource',
            'hierarchies',
            hierarchyIndex,
            'columnSortConfig',
          ]);

          delete existingConfig[columnName];
          set(series, ['dataSource', 'hierarchies', hierarchyIndex, 'columnSortConfig'], existingConfig);
        } else {
          update(
            series,
            ['dataSource', 'hierarchies', hierarchyIndex, 'columnSortConfig'],
            (config = {}) => ({
              ...config,
              [columnName]: rowData,
            }),
          );
        }
      });
      break;
    }

    case actions.SET_TABLE_ORDER_CONFIG: {
      const { hierarchyId, orderConfig }: { hierarchyId: string | undefined; orderConfig: OrderConfig[] } =
        action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        const hierarchies = get(series, ['dataSource', 'hierarchies'], []);
        const hierarchyIndex = hierarchies.findIndex((hierachy) => hierachy.id === hierarchyId);
        if (hierarchyIndex < 0) {
          // hierarchies.findIndex() will return -1 if there are no hierarchies found.
          // This if-block is done to prevent negative indices in the hierarchies array.
          // If the custom sort configuration does not apply due to this if-block,
          // it might be due to the vif not having hierarchies, which is something worse.
          return;
        }
        const nonNullOrderConfig = orderConfig.filter(
          (conf) => !has(conf, 'sortToFollow') || !isNil(conf.sortToFollow),
        );
        const dataSourceType = get(series, 'dataSource.type');

        if (dataSourceType !== 'socrata.soql' && dataSourceType !== 'socrata.rawSoql') {
          return;
        } else {
          const activeHierarchyId = get(series, 'dataSource.activeHierarchyId');
          const activeHierarchyIndex = hierarchies.findIndex((h: Hierarchy) => h.id == activeHierarchyId);

          if (activeHierarchyId !== hierarchyId) {
            return;
          }

          const existingHierarchyOrder = hierarchies[activeHierarchyIndex].order || [];
          if (!isEqual(existingHierarchyOrder, nonNullOrderConfig)) {
            set(series, `dataSource.hierarchies[${activeHierarchyIndex}].order`, nonNullOrderConfig);
          }
        }
      });
      break;
    }

    case actions.SET_TABLE_HEADER_SORT_CYCLE_INDEX:
      const {
        headerSortCycleIndex,
        hierarchyId,
      }: { headerSortCycleIndex: number; hierarchyId: string | undefined } = action;
      forEachSeries(state, (series: Vif['series'][number]) => {
        const hierarchies = get(series, ['dataSource', 'hierarchies'], []);
        const hierarchyIndex = hierarchies.findIndex((hierachy) => hierachy.id === hierarchyId);
        if (hierarchyIndex < 0) {
          return;
        }
        const dataSourceType = get(series, 'dataSource.type');
        if (dataSourceType !== 'socrata.soql' && dataSourceType !== 'socrata.rawSoql') {
          return;
        }
        const activeHierarchyId = get(series, 'dataSource.activeHierarchyId');
        const activeHierarchyIndex = hierarchies.findIndex((h: Hierarchy) => h.id == activeHierarchyId);
        if (activeHierarchyId !== hierarchyId) {
          return;
        }
        set(
          series,
          `dataSource.hierarchies[${activeHierarchyIndex}].headerSortCycleIndex`,
          headerSortCycleIndex,
        );
      });
      break;

    case actions.ADD_TABLE_COLUMN:
    case actions.SET_DESCRIPTION:
    case actions.REMOVE_TABLE_COLUMN:
    case actions.REPLACE_ALL_COLUMNS:
    case actions.RESET_ALL_COLUMNS:
    case actions.RECEIVE_METADATA:
    case actions.REORDER_TABLE_COLUMNS:
    case actions.SET_DOMAIN:
    case actions.SET_DATASET_UID:
    case actions.SET_FILTERS:
    case actions.SET_TITLE:
    case actions.SET_UNIT_ONE:
    case actions.SET_UNIT_OTHER:
    case actions.SET_VIEW_SOURCE_DATA_LINK:
      return baseVifReducer(state, action);
    default:
      break;
  }

  return state;
}
